// SPDX-License-Identifier: Apache-2.0

// Audit log viewer (Hestia).
//
// Reads from `GET http://localhost:5417/api/audit?limit=200&offset=0`.
// Renders a virtualized table — one row per dispatch, newest first.
// Filter row at top (by tool name, by decision). Auto-refreshes every 5 s.
// Export button downloads the raw JSONL via /api/audit/export.
//
// Hestia rules baked in:
//   - The audit page is a TABLE, not a paragraph.
//   - Two-line max in any UI string.
//   - Virtualized at 1000+ rows: <For> with a sliding window of 100 entries.

import { For, Show, createMemo, createResource, createSignal, onCleanup } from "solid-js";
import { api } from "../lib/api";

interface AuditRow {
  ts: string;
  tool: string;
  args_hash: string;
  decision: "allow" | "deny";
  reason: string | null;
  duration_ms: number;
  transport: "stdio" | "ws" | "sse-internal";
  client_id: string;
  approval_id?: string | null;
}

interface AuditResponse {
  rows: AuditRow[];
  total: number;
  hasMore: boolean;
}

// Use relative URLs so dev goes through Vite's /api proxy (which injects the
// Origin header) and prod goes through CF Pages Functions / local MCP — both
// avoid CORS preflight by being same-origin.
const WINDOW_SIZE = 100;
const REFRESH_MS = 5000;

async function fetchAudit(params: {
  limit: number;
  tool: string;
  decision: string;
}): Promise<AuditResponse> {
  const qs = new URLSearchParams();
  qs.set("limit", String(params.limit));
  if (params.tool) qs.set("tool", params.tool);
  if (params.decision) qs.set("decision", params.decision);
  const r = await api(`/api/audit?${qs.toString()}`, { headers: { accept: "application/json" } });
  if (!r.ok) throw new Error(`audit ${r.status}`);
  return (await r.json()) as AuditResponse;
}

function formatTs(iso: string): string {
  // Compact local time; full ISO on hover via title.
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
}

function shortHash(h: string | undefined): string {
  if (!h) return "";
  // sha256:9f1c… → 9f1c…
  const colon = h.indexOf(":");
  const tail = colon >= 0 ? h.slice(colon + 1) : h;
  return tail.slice(0, 8);
}

export interface AuditLogProps {
  /**
   * Optional filter that PINS the underlying query — used by ChatHistory
   * to scope the table to `{ tool: 'brain_chat' }` without exposing the
   * filter row to the user. When set, the user-facing filter inputs are
   * hidden and the count line shows the pinned scope.
   *
   * If you just want a pre-filled (but still user-editable) filter, pass
   * `defaultFilter` instead.
   */
  filter?: { tool?: string; decision?: "allow" | "deny" };
  /** Pre-fill the filter inputs but leave them editable. */
  defaultFilter?: { tool?: string; decision?: "allow" | "deny" };
}

export default function AuditLog(props: AuditLogProps = {}) {
  const pinned = () => props.filter;
  const [tool, setTool] = createSignal(props.defaultFilter?.tool ?? "");
  const [decision, setDecision] = createSignal(props.defaultFilter?.decision ?? "");
  const [windowStart, setWindowStart] = createSignal(0);

  const effectiveTool = () => pinned()?.tool ?? tool();
  const effectiveDecision = () => pinned()?.decision ?? decision();

  const [data, { refetch }] = createResource(
    () => ({ tool: effectiveTool(), decision: effectiveDecision(), limit: 1000 }),
    fetchAudit
  );

  const interval = setInterval(() => {
    void refetch();
  }, REFRESH_MS);
  onCleanup(() => clearInterval(interval));

  const rows = createMemo<AuditRow[]>(() => data()?.rows ?? []);
  const total = createMemo<number>(() => data()?.total ?? 0);
  const visible = createMemo<AuditRow[]>(() => {
    const all = rows();
    const start = Math.min(windowStart(), Math.max(0, all.length - WINDOW_SIZE));
    return all.slice(start, start + WINDOW_SIZE);
  });

  const onExport = () => {
    const a = document.createElement("a");
    a.href = `/api/audit/export`;
    a.download = "tether-audit.jsonl";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div class="hestia-audit">
      <div class="hestia-audit__bar">
        <Show when={!pinned()}>
          <input
            class="hestia-audit__filter"
            type="text"
            placeholder="filter by tool"
            value={tool()}
            onInput={(e) => {
              setTool(e.currentTarget.value);
              setWindowStart(0);
            }}
          />
          <select
            class="hestia-audit__filter hestia-audit__filter--decision"
            value={decision()}
            onChange={(e) => {
              setDecision(e.currentTarget.value);
              setWindowStart(0);
            }}
          >
            <option value="">any decision</option>
            <option value="allow">allow</option>
            <option value="deny">deny</option>
          </select>
        </Show>
        <Show when={pinned()}>
          <span class="hestia-audit__count">
            scope: tool={pinned()?.tool ?? "*"}
            {pinned()?.decision ? ` · ${pinned()?.decision}` : ""}
          </span>
        </Show>
        <span class="hestia-audit__count">
          {total()} rows · showing {visible().length}
        </span>
        <button type="button" class="hestia-audit__export" onClick={onExport}>
          export jsonl
        </button>
      </div>

      <Show when={data.error}>
        <div class="hestia-audit__error">
          mcp unreachable at /api. is the brain running?
        </div>
      </Show>

      <div class="hestia-audit__table" role="table" aria-label="audit log">
        <div class="hestia-audit__head" role="row">
          <span role="columnheader">time</span>
          <span role="columnheader">tool</span>
          <span role="columnheader">decision</span>
          <span role="columnheader">transport</span>
          <span role="columnheader">duration</span>
          <span role="columnheader">args</span>
        </div>
        <Show
          when={visible().length > 0}
          fallback={
            <div class="hestia-audit__empty">
              no rows yet — every read, write, send will land here.
            </div>
          }
        >
          <For each={visible()}>
            {(row) => (
              <div class="hestia-audit__row" role="row" title={row.ts}>
                <span class="hestia-audit__ts" role="cell">
                  {formatTs(row.ts)}
                </span>
                <span class="hestia-audit__tool" role="cell">
                  {row.tool}
                </span>
                <span
                  class={`hestia-audit__decision hestia-audit__decision--${row.decision}`}
                  role="cell"
                >
                  {row.decision}
                </span>
                <span class="hestia-audit__transport" role="cell">
                  {row.transport}
                </span>
                <span class="hestia-audit__duration" role="cell">
                  {row.duration_ms}ms
                </span>
                <span class="hestia-audit__args" role="cell">
                  {shortHash(row.args_hash)}
                </span>
              </div>
            )}
          </For>
        </Show>
      </div>

      <Show when={rows().length > WINDOW_SIZE}>
        <div class="hestia-audit__paging">
          <button
            type="button"
            disabled={windowStart() === 0}
            onClick={() => setWindowStart(Math.max(0, windowStart() - WINDOW_SIZE))}
          >
            newer
          </button>
          <span class="hestia-audit__pagepos">
            {windowStart() + 1}–{Math.min(windowStart() + WINDOW_SIZE, rows().length)}
            {" / "}
            {rows().length}
          </span>
          <button
            type="button"
            disabled={windowStart() + WINDOW_SIZE >= rows().length}
            onClick={() =>
              setWindowStart(
                Math.min(rows().length - WINDOW_SIZE, windowStart() + WINDOW_SIZE)
              )
            }
          >
            older
          </button>
        </div>
      </Show>
    </div>
  );
}
